import { PrimitiveAtom, useAtom } from 'jotai'
import { useEffect, useState } from 'react'

import DrawerDialog from '@/components/drawer'
import { DrawerBody, DrawerFooter, DrawerHeader } from '@/components/ui/drawer'
import useColorModeValue from '@/hooks/useColorModeValue'
import {
    getItemDetails,
    isPlusOneApprovalAtom,
    itemsForBulkApprovalsAtom,
    selectedDetailsItemAtom,
    sharepointPermisionAtom,
    showDetailsDrawerAtom,
    showSailPointCertDisclaimerAtom,
} from '@/store/tasksApprovals/detailsDrawer'
import { IApproval, IApprovalDetail, ICompliance, ISailPointCertDetails, ITaskDetail, TMainSections } from '@/types/tasksApprovals'
import { isSailPointCert, isSailPointCertDecision, isSapGrcAccessReques, isTNE } from '@/utils/approvals'

import BodyContent from './body'
import DetailsSkeleton from './body/layouts/components/detailsSkeleton'
import FooterContent from './footer'
import HeaderContent from './header'

const Container = () => {
    const [item] = useAtom<PrimitiveAtom<ICompliance | IApproval>>(selectedDetailsItemAtom)

    const [isOpen, showDetailsDrawer] = useAtom(showDetailsDrawerAtom)
    const [itemDetails, setItemDetails] = useState<ITaskDetail | IApprovalDetail | ISailPointCertDetails[] | null>(null)
    const [, setItemsForBulkApprovals] = useAtom(itemsForBulkApprovalsAtom)
    const [, resetSharepointPermision] = useAtom(sharepointPermisionAtom)
    const [, setIsPlusOneApproval] = useAtom(isPlusOneApprovalAtom)
    const [, setShowSailPointCertDisclaimerAtom] = useAtom(showSailPointCertDisclaimerAtom)

    const borderColor = useColorModeValue('gray.300', 'gray.700')

    let _itemId = ''
    let _type: TMainSections
    if (item) {
        if ('compliance_original_id' in item) {
            _itemId = item.compliance_original_id
            _type = 'compliances'
        } else {
            _itemId = item.approval_entity_id
            _type = 'approvals'
        }
    }

    useEffect(() => {
        async function getDetails() {
            const res = await getItemDetails<IApprovalDetail | ITaskDetail | ISailPointCertDetails[]>(item.system_id, _itemId, _type)
            if (res && !('ok' in res)) {
                if (_type === 'approvals') {
                    const _res = res as IApprovalDetail
                    if (_res.metadata && 'isPlusOneApproval' in _res.metadata && _res.metadata.isPlusOneApproval) {
                        setIsPlusOneApproval(true)
                    }
                }
                if (Array.isArray(res) && res[0].system_id === 'SAIL_POINT_CERT_DETAILS') {
                    if (res.some((detail) => detail.metadata?.isDisabled === true)) {
                        setShowSailPointCertDisclaimerAtom(true)
                    }
                }

                setItemDetails(res)
                !isSailPointCert(item) && setItemsForBulkApprovals([_itemId])
            } else if (res && 'ok' in res && !res.ok) {
                onClose()
            }
        }
        isOpen && void getDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.system_id, isOpen])

    const onClose = () => {
        setItemsForBulkApprovals([])
        showDetailsDrawer(false)
        setItemDetails(null)
        resetSharepointPermision('view')
        setIsPlusOneApproval(false)
    }

    if (!item || (!itemDetails && item.system_id === 'SAIL_POINT_CERT')) return

    const drawerSize =
        isSailPointCert(item) || isSailPointCertDecision(item) || isSapGrcAccessReques(item) ? 'lg' : isTNE(item) ? 'xl' : 'sm'

    return (
        <DrawerDialog isOpen={isOpen} onClose={onClose} noPadding={true} size={drawerSize}>
            <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} mx="24px" py="24px" px="0">
                <HeaderContent item={item} />
            </DrawerHeader>
            <DrawerBody px="0" pb="24px" pt={isSailPointCert(item) ? '0' : '24px'}>
                {!itemDetails && <DetailsSkeleton />}
                {itemDetails && <BodyContent item={item} itemDetails={itemDetails} itemId={_itemId} />}
            </DrawerBody>
            <DrawerFooter borderTopWidth="1px" borderColor={borderColor} mx="24px" px="0" py="24px" display="block">
                <FooterContent item={item} itemDetails={itemDetails} itemId={_itemId} onClose={onClose} />
            </DrawerFooter>
        </DrawerDialog>
    )
}

export default Container
